import React, { useEffect, useState } from 'react';
import { PropTypes } from "prop-types";

import Button from '../Button';

import arrowLeft from '../../../assets/icons/chevron-left.svg';
import arrowRight from '../../../assets/icons/chevron-right.svg';

import './styles.scss';

const Table = ({ 
  className, 
  children, 
  pagination, 
  selectedPage, 
  onPageChange, 
  pageSize, 
  totalRows, 
  categoryName, 
  ...rest 
}) => {

  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(selectedPage || 1);

  // ✅ Sync with parent page (important)
  useEffect(() => {
    if (selectedPage) setPage(selectedPage);
  }, [selectedPage]);

  // ✅ Calculate total pages
  useEffect(() => {
    if (pagination) {
      const pages = Math.ceil(totalRows / pageSize);
      setTotalPages(pages);

      if (page > pages) {
        setPage(1);
        onPageChange(1);
      }
    }
  }, [totalRows, pageSize]);

  // ✅ Pagination handlers
  const handleNext = () => {
    if (page < totalPages) {
      const newPage = page + 1;
      setPage(newPage);
      onPageChange(newPage);
    }
  };

  const handlePrev = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      onPageChange(newPage);
    }
  };

  const handlePageClick = (newPage) => {
    setPage(newPage);
    onPageChange(newPage);
  };

  // ✅ Visible page numbers
  const MAX_VISIBLE_PAGES = 3;

  const getVisiblePages = () => {
    let start = Math.max(1, page - 1);
    let end = start + MAX_VISIBLE_PAGES - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - MAX_VISIBLE_PAGES + 1);
    }

    return Array.from(
      { length: end - start + 1 },
      (_, i) => start + i
    );
  };

  // ✅ CORE FIX: Slice rows using React (NOT DOM)
  const renderPaginatedChildren = () => {
    if (!pagination) return children;

    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    const childrenArray = React.Children.toArray(children);

    return childrenArray.map((child) => {
      if (child.type === 'tbody') {
        const rows = React.Children.toArray(child.props.children);
        const paginatedRows = rows.slice(start, end);

        return React.cloneElement(child, {}, paginatedRows);
      }
      return child;
    });
  };

  // ✅ Showing count
  const indexOfLastItem = page * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const showingFrom = totalRows === 0 ? 0 : indexOfFirstItem + 1;
  const showingTo = Math.min(indexOfLastItem, totalRows);

  return (
    <div className={`v-table table-responsive ${className}`} {...rest}>
      <table>
        {renderPaginatedChildren()}
      </table>

      {pagination && totalPages > 1 && (
        <div className='b-pagination'>
          <div className='count-show'>
            {`Showing ${showingFrom}-${showingTo} of ${totalRows} ${categoryName}`}
          </div>

          <div className='numbers'>
            <Button disabled={page === 1} type="primary" onClick={handlePrev}>
              <img src={arrowLeft} alt="Previous" />
            </Button>

            {getVisiblePages().map((pageNumber) => (
              <Button
                key={pageNumber}
                className={page === pageNumber ? 'active-page' : 'non-active-page'}
                onClick={() => handlePageClick(pageNumber)}
              >
                {pageNumber}
              </Button>
            ))}

            <Button disabled={page === totalPages} type="primary" onClick={handleNext}>
              <img src={arrowRight} alt="Next" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  pagination: PropTypes.bool,
  pageSize: PropTypes.number,
  totalRows: PropTypes.number,
  onPageChange: PropTypes.func,
  categoryName: PropTypes.string,
};

Table.defaultProps = {
  className: '',
  children: <></>,
  pagination: false,
  pageSize: 10,
  totalRows: 0,
  onPageChange: () => {},
  categoryName: 'Items',
};

export default Table;